import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, Search, Phone, Mail, MapPin, 
  RefreshCw, X, User, Smartphone, UserPlus,
  ChevronDown, Clock, CheckCircle, AlertCircle, Home,
  Edit2, Trash2, Eye, MoreVertical, Star, Car,
  ChevronRight, ArrowLeft, History, CreditCard, Calendar, Lock,
  Globe, PhoneCall
} from 'lucide-react';
import api from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { formatLek, formatEuro, lekToEuro } from '../../utils/formatCurrency';
import { useTableSort, sortRows } from '../../hooks/useTableSort';
import { SortableTableHead } from '../ui/SortableTableHead';

const PASSENGER_TABLE_SORT = {
  name: (p) => (p.name || '').toLowerCase(),
  type: (p) => (p.passenger_type || 'walk-in').toLowerCase(),
  source: (p) => (p.source || '').toLowerCase(),
  contact: (p) => (p.phone || p.email || '').toLowerCase(),
  rides: (p) => Number(p.total_rides || 0),
  revenue: (p) => Number(p.total_revenue || 0),
  registered: (p) => new Date(p.created_at || 0).getTime(),
};

// Albania popular locations for home address suggestions
const ALBANIA_LOCATIONS = [
  { name: 'Blloku', address: 'Blloku, Tiranë', lat: 41.3195, lng: 19.8227 },
  { name: 'Rruga Myslym Shyri', address: 'Rruga Myslym Shyri, Tiranë', lat: 41.3248, lng: 19.8262 },
  { name: 'Sheshi Skënderbej', address: 'Sheshi Skënderbej, Tiranë', lat: 41.3275, lng: 19.8189 },
  { name: 'Komuna e Parisit', address: 'Komuna e Parisit, Tiranë', lat: 41.3220, lng: 19.8150 },
  { name: 'Rruga e Durrësit', address: 'Rruga e Durrësit, Tiranë', lat: 41.3290, lng: 19.8140 },
  { name: 'Don Bosko', address: 'Don Bosko, Tiranë', lat: 41.3150, lng: 19.8080 },
  { name: '21 Dhjetori', address: '21 Dhjetori, Tiranë', lat: 41.3310, lng: 19.8170 },
  { name: 'Rruga e Elbasanit', address: 'Rruga e Elbasanit, Tiranë', lat: 41.3180, lng: 19.8350 },
  { name: 'Medreseja', address: 'Medreseja, Tiranë', lat: 41.3240, lng: 19.8320 },
  { name: 'Selitë', address: 'Selitë, Tiranë', lat: 41.3420, lng: 19.7950 }
];

// Home Address Input with suggestions
const HomeAddressInput = ({ value, onChange }) => {
  const [inputText, setInputText] = useState(value?.address || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const inputRef = useRef(null);
  const isFocusedRef = useRef(false);
  const addressKey = value?.address ?? '';

  useEffect(() => {
    if (isFocusedRef.current) return;
    setInputText(addressKey);
  }, [addressKey]);

  useEffect(() => {
    if (inputText.length > 1) {
      const filtered = ALBANIA_LOCATIONS.filter(loc => 
        loc.name.toLowerCase().includes(inputText.toLowerCase()) ||
        loc.address.toLowerCase().includes(inputText.toLowerCase())
      );
      setFilteredLocations(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [inputText]);

  const handleSelect = (location) => {
    setInputText(location.address);
    onChange({
      address: location.address,
      lat: location.lat,
      lng: location.lng
    });
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    if (text.trim()) {
      onChange({
        address: text,
        lat: 41.3275,
        lng: 19.8187
      });
    } else {
      onChange(null);
    }
  };

  return (
    <div className="relative">
      <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
        <Home className="w-4 h-4 text-blue-500" />
        Adresa e Shtëpisë
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => {
            isFocusedRef.current = true;
            if (inputText.length > 1 && filteredLocations.length > 0) setShowSuggestions(true);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
          }}
          placeholder="P.sh. Blloku, Rruga Myslym Shyri..."
          className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none pr-10"
          autoComplete="off"
        />
        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>
      
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filteredLocations.map((loc, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(loc)}
              className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 border-b border-gray-100 last:border-0"
            >
              <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-800">{loc.name}</p>
                <p className="text-xs text-gray-500">{loc.address}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Passenger Profile Modal
const PassengerProfile = ({ passenger, onClose, onEdit, onDelete, exchangeRate }) => {
  const [rides, setRides] = useState([]);
  const [loadingRides, setLoadingRides] = useState(true);

  useEffect(() => {
    fetchPassengerRides();
  }, [passenger.id]);

  const fetchPassengerRides = async () => {
    try {
      setLoadingRides(true);
      const response = await api.get(`/rides?passenger_id=${passenger.id}&limit=20`);
      setRides(response.data || []);
    } catch (error) {
      console.error('Error fetching rides:', error);
    } finally {
      setLoadingRides(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="passenger-profile-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                passenger.passenger_type === 'app' ? 'bg-green-500' :
                passenger.passenger_type === 'website' ? 'bg-purple-500' :
                passenger.passenger_type === 'phone' ? 'bg-sky-500' :
                'bg-orange-500'
              }`}>
                {passenger.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{passenger.name || 'Pa emër'}</h2>
                <div className="flex items-center gap-3 mt-1">
                  {passenger.passenger_type === 'app' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/30">
                      <Smartphone size={12} />
                      App
                    </span>
                  ) : passenger.passenger_type === 'website' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/30">
                      <Globe size={12} />
                      Website
                    </span>
                  ) : passenger.passenger_type === 'phone' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-sky-500/30">
                      <PhoneCall size={12} />
                      Telefon
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-500/30">
                      <User size={12} />
                      Walk-in
                    </span>
                  )}
                  {passenger.verified_email && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-200">
                      <CheckCircle size={12} />
                      Email i verifikuar
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(passenger)}
                className="p-2 hover:bg-white/20 rounded-lg transition"
                title="Modifiko"
              >
                <Edit2 size={20} />
              </button>
              <button
                onClick={() => onDelete(passenger)}
                className="p-2 hover:bg-red-500/50 rounded-lg transition"
                title="Fshi"
              >
                <Trash2 size={20} />
              </button>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition ml-2">
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Car className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{passenger.total_rides || 0}</div>
                  <div className="text-sm text-gray-500">Udhëtime</div>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{formatLek(passenger.total_revenue || 0)}</div>
                  <div className="text-xs text-gray-500">{formatEuro(lekToEuro(passenger.total_revenue || 0, exchangeRate))}</div>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{(passenger.rating || 5).toFixed(1)}</div>
                  <div className="text-sm text-gray-500">Vlerësimi</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <User size={18} />
              Informacioni i Kontaktit
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Telefoni</div>
                  <div className="font-medium">{passenger.phone || '—'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-medium">{passenger.email || '—'}</div>
                </div>
              </div>
              {passenger.home_address && (
                <div className="flex items-center gap-3 col-span-2">
                  <Home size={16} className="text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Adresa e Shtëpisë</div>
                    <div className="font-medium">{passenger.home_address.address}</div>
                  </div>
                </div>
              )}
              {passenger.notes && (
                <div className="col-span-2">
                  <div className="text-xs text-gray-500 mb-1">Shënime</div>
                  <div className="text-sm text-gray-700 bg-white p-2 rounded border">{passenger.notes}</div>
                </div>
              )}
            </div>
          </div>

          {/* Ride History */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <History size={18} />
              Historiku i Udhëtimeve
            </h3>
            {loadingRides ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Duke ngarkuar...
              </div>
            ) : rides.length === 0 ? (
              <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl">
                <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Asnjë udhëtim i regjistruar</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {rides.map((ride) => (
                  <div key={ride.id} className="bg-white border rounded-lg p-3 hover:border-blue-200 transition">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin size={14} className="text-green-500" />
                          <span className="text-gray-600 truncate max-w-[200px]">{ride.pickup_address || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm mt-1">
                          <MapPin size={14} className="text-red-500" />
                          <span className="text-gray-600 truncate max-w-[200px]">{ride.dropoff_address || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-green-600">{formatLek(ride.final_price || ride.estimated_price || 0)}</div>
                        <div className="text-xs text-gray-400">
                          {ride.created_at ? new Date(ride.created_at).toLocaleDateString('en-GB') : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Regjistruar: {passenger.created_at ? new Date(passenger.created_at).toLocaleDateString('sq-AL') : '—'}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition"
          >
            Mbyll
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ passenger, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Fshi Pasagjerin?</h3>
        <p className="text-gray-600 mb-6">
          Jeni të sigurt që dëshironi të fshini pasagjerin <strong>{passenger.name}</strong>? 
          Ky veprim nuk mund të kthehet mbrapsht.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition"
            disabled={loading}
          >
            Anulo
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-xl font-medium text-white transition flex items-center justify-center gap-2"
            disabled={loading}
          >
            {loading ? <RefreshCw size={18} className="animate-spin" /> : <Trash2 size={18} />}
            Fshi
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Set Password Modal
const SetPasswordModal = ({ passenger, onClose, onSave, loading }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!password) {
      setError('Ju lutem vendosni një fjalëkalim');
      return;
    }
    
    if (password.length < 6) {
      setError('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Fjalëkalimet nuk përputhen');
      return;
    }
    
    onSave(password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-500" />
            Vendos Fjalëkalim
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Vendosni një fjalëkalim të ri për pasagjerin <strong>{passenger.name}</strong>
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Password */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Fjalëkalimi *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Të paktën 6 karaktere"
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <Eye size={18} /> : <Eye size={18} className="opacity-50" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Konfirmo Fjalëkalimin *</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Përsërite fjalëkalimin"
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <RefreshCw size={18} className="animate-spin" /> : <Lock size={18} />}
            {loading ? 'Duke ruajtur...' : 'Ruaj Fjalëkalimin'}
          </button>
        </form>
      </div>
    </div>
  );
};

const EMPTY_PASSENGER_FORM = {
  name: '',
  phone: '',
  email: '',
  passenger_type: 'walk-in',
  notes: '',
  home_address: null,
  password: '',
};

const passengerToFormData = (passenger) => ({
  name: passenger?.name || '',
  phone: passenger?.phone || '',
  email: passenger?.email || '',
  passenger_type: passenger?.passenger_type || 'walk-in',
  notes: passenger?.notes || '',
  home_address: passenger?.home_address || null,
  password: '',
});

// Form state lives inside the modal so parent list re-renders do not steal input focus.
const PassengerFormModal = ({ isEdit, initialData, onSubmit, onClose }) => {
  const [formData, setFormData] = useState(() =>
    initialData ? passengerToFormData(initialData) : { ...EMPTY_PASSENGER_FORM }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const modal = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            {isEdit ? <Edit2 className="w-5 h-5 text-blue-500" /> : <UserPlus className="w-5 h-5 text-blue-500" />}
            {isEdit ? 'Modifiko Pasagjerin' : 'Shto Pasagjer të Ri'}
          </h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Lloji i Pasagjerit</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, passenger_type: 'walk-in' }))}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition ${
                  formData.passenger_type === 'walk-in' || !formData.passenger_type
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <User className="w-5 h-5 mx-auto mb-1" />
                Walk-in
              </button>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, passenger_type: 'app' }))}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition ${
                  formData.passenger_type === 'app'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Smartphone className="w-5 h-5 mx-auto mb-1" />
                App
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Emri *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Emri i plotë"
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Telefoni</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="+355 69 123 4567"
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="email@example.com"
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {!isEdit && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Fjalëkalim (Opsional)
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                placeholder="Opsional - mund të vendoset më vonë"
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
              {formData.password && formData.password.length < 6 && (
                <p className="text-xs text-orange-600 mt-1">Fjalëkalimi duhet të ketë të paktën 6 karaktere</p>
              )}
            </div>
          )}

          <HomeAddressInput
            value={formData.home_address}
            onChange={(addr) => setFormData((prev) => ({ ...prev, home_address: addr }))}
          />

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Shënime</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Shënime shtesë..."
              rows={2}
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2"
          >
            {isEdit ? <Edit2 size={18} /> : <UserPlus size={18} />}
            {isEdit ? 'Ruaj Ndryshimet' : 'Regjistro Pasagjerin'}
          </button>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

// Main Component
const PassengersList = () => {
  const { formatDateTime, formatTime, settings } = useSettings();
  const exchangeRate = settings?.exchange_rate || 100;
  
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const { sortKey, sortDir, onSort } = useTableSort('registered', 'desc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, walkIn: 0, app: 0, website: 0, phone: 0 });
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  useEffect(() => {
    fetchPassengers();
  }, []);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchPassengers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/passengers?limit=200');
      const data = response.data || [];
      setPassengers(data);
      
      const walkIn = data.filter(p => p.passenger_type === 'walk-in' || !p.passenger_type).length;
      const app = data.filter(p => p.passenger_type === 'app').length;
      const website = data.filter(p => p.passenger_type === 'website').length;
      const phone = data.filter(p => p.passenger_type === 'phone').length;
      setStats({
        total: data.length,
        walkIn,
        app,
        website,
        phone
      });
    } catch (error) {
      console.error('Error fetching passengers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPassenger = async (formData) => {
    if (!formData.name) {
      alert('Ju lutem vendosni emrin');
      return;
    }
    try {
      await api.post('/passengers', { ...formData, source: 'manual' });
      setShowAddModal(false);
      fetchPassengers();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditPassenger = async (formData) => {
    if (!selectedPassenger) return;

    try {
      await api.put(`/passengers/${selectedPassenger.id}`, formData);
      setShowEditModal(false);
      setSelectedPassenger(null);
      fetchPassengers();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePassenger = async () => {
    if (!selectedPassenger) return;
    
    try {
      setDeleteLoading(true);
      await api.delete(`/passengers/${selectedPassenger.id}`);
      setShowDeleteModal(false);
      setShowProfileModal(false);
      setSelectedPassenger(null);
      fetchPassengers();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSetPassword = async (password) => {
    if (!selectedPassenger) return;
    
    try {
      setPasswordLoading(true);
      await api.post(`/passengers/${selectedPassenger.id}/set-password`, { password });
      setShowPasswordModal(false);
      alert('Fjalëkalimi u vendos me sukses!');
      fetchPassengers();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPasswordLoading(false);
    }
  };

  const openEditModal = (passenger) => {
    setSelectedPassenger(passenger);
    setShowEditModal(true);
    setShowProfileModal(false);
    setActionMenuOpen(null);
  };

  const openDeleteModal = (passenger) => {
    setSelectedPassenger(passenger);
    setShowDeleteModal(true);
    setShowProfileModal(false);
    setActionMenuOpen(null);
  };

  const openProfileModal = (passenger) => {
    setSelectedPassenger(passenger);
    setShowProfileModal(true);
    setActionMenuOpen(null);
  };

  const filteredPassengers = useMemo(() => passengers.filter((p) => {
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.phone?.includes(searchTerm) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || 
      (filterType === 'walk-in' && (p.passenger_type === 'walk-in' || !p.passenger_type)) ||
      (filterType === 'app' && p.passenger_type === 'app') ||
      (filterType === 'website' && p.passenger_type === 'website') ||
      (filterType === 'phone' && p.passenger_type === 'phone');
    
    return matchesSearch && matchesType;
  }), [passengers, searchTerm, filterType]);

  const displayedPassengers = useMemo(
    () => sortRows(filteredPassengers, sortKey, sortDir, PASSENGER_TABLE_SORT),
    [filteredPassengers, sortKey, sortDir],
  );

  return (
    <div className="p-6" data-testid="passengers-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              Pasagjerët
            </h1>
            <p className="text-gray-600">Menaxhimi i pasagjerëve walk-in dhe app</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl font-semibold transition shadow-lg"
            data-testid="add-passenger-btn"
          >
            <UserPlus size={20} />
            Shto Pasagjer
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div 
            className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition ${filterType === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setFilterType('all')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition ${filterType === 'walk-in' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setFilterType('walk-in')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">{stats.walkIn}</div>
                <div className="text-sm text-gray-500">Walk-in</div>
              </div>
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition ${filterType === 'app' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setFilterType('app')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{stats.app}</div>
                <div className="text-sm text-gray-500">App</div>
              </div>
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition ${filterType === 'website' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setFilterType('website')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{stats.website}</div>
                <div className="text-sm text-gray-500">Website</div>
              </div>
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition ${filterType === 'phone' ? 'border-sky-500 bg-sky-50' : 'border-gray-200 hover:border-gray-300'}`}
            onClick={() => setFilterType('phone')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                <PhoneCall className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-sky-600">{stats.phone}</div>
                <div className="text-sm text-gray-500">Telefon</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Kërko pasagjer... (emër, telefon, email)"
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
            data-testid="search-passengers"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="passengers-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <SortableTableHead label="Pasagjeri" field="name" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortableTableHead label="Lloji" field="type" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
                <SortableTableHead label="Burimi" field="source" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
                <SortableTableHead label="Kontakti" field="contact" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortableTableHead label="Udhëtime" field="rides" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
                <SortableTableHead label="Xhiro Totale" field="revenue" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" />
                <SortableTableHead label="Regjistruar" field="registered" activeField={sortKey} sortDir={sortDir} onSort={onSort} />
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Duke ngarkuar...
                  </td>
                </tr>
              ) : displayedPassengers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    {searchTerm || filterType !== 'all' 
                      ? 'Nuk u gjet asnjë pasagjer me këtë kërkim'
                      : 'Nuk ka pasagjerë të regjistruar'}
                  </td>
                </tr>
              ) : (
                displayedPassengers.map((passenger) => (
                  <tr 
                    key={passenger.id} 
                    className="hover:bg-gray-50 cursor-pointer" 
                    data-testid={`passenger-row-${passenger.id}`}
                    onClick={() => openProfileModal(passenger)}
                  >
                    {/* Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          passenger.passenger_type === 'app' ? 'bg-green-500' :
                          passenger.passenger_type === 'website' ? 'bg-purple-500' :
                          passenger.passenger_type === 'phone' ? 'bg-sky-500' :
                          'bg-orange-500'
                        }`}>
                          {passenger.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{passenger.name || 'Pa emër'}</div>
                          {passenger.notes && (
                            <div className="text-xs text-gray-400 truncate max-w-[200px]">{passenger.notes}</div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3 text-center">
                      {passenger.passenger_type === 'app' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Smartphone size={12} />
                          App
                        </span>
                      ) : passenger.passenger_type === 'website' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          <Globe size={12} />
                          Website
                        </span>
                      ) : passenger.passenger_type === 'phone' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                          <PhoneCall size={12} />
                          Telefon
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                          <User size={12} />
                          Walk-in
                        </span>
                      )}
                    </td>

                    {/* Source */}
                    <td className="px-4 py-3 text-center">
                      {passenger.source === 'app' || passenger.source === 'passenger_app' ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          <Smartphone size={12} />
                          Aplikacion
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          <UserPlus size={12} />
                          Manual
                        </span>
                      )}
                    </td>

                    {/* Contact */}
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {passenger.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone size={14} className="text-gray-400" />
                            {passenger.phone}
                          </div>
                        )}
                        {passenger.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail size={14} className="text-gray-400" />
                            {passenger.email}
                          </div>
                        )}
                        {!passenger.phone && !passenger.email && (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </div>
                    </td>

                    {/* Rides count */}
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold text-gray-900">{passenger.total_rides || 0}</span>
                    </td>

                    {/* Total Revenue - Formatted */}
                    <td className="px-4 py-3 text-center">
                      <div>
                        <div className="font-semibold text-green-600">{formatLek(passenger.total_revenue || 0)}</div>
                        <div className="text-xs text-gray-400">{formatEuro(lekToEuro(passenger.total_revenue || 0, exchangeRate))}</div>
                      </div>
                    </td>

                    {/* Created */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">
                        {passenger.created_at 
                          ? new Date(passenger.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—'
                        }
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenuOpen(actionMenuOpen === passenger.id ? null : passenger.id);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg transition"
                          data-testid={`passenger-actions-${passenger.id}`}
                        >
                          <MoreVertical size={18} className="text-gray-500" />
                        </button>
                        
                        {actionMenuOpen === passenger.id && (
                          <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20">
                            <button
                              onClick={() => openProfileModal(passenger)}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 rounded-t-xl"
                            >
                              <Eye size={16} className="text-blue-500" />
                              Shiko Profilin
                            </button>
                            <button
                              onClick={() => openEditModal(passenger)}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Edit2 size={16} className="text-green-500" />
                              Modifiko
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPassenger(passenger);
                                setShowPasswordModal(true);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                            >
                              <Lock size={16} className="text-purple-500" />
                              Vendos Fjalëkalim
                            </button>
                            <button
                              onClick={() => openDeleteModal(passenger)}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2 rounded-b-xl"
                            >
                              <Trash2 size={16} />
                              Fshi
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Duke shfaqur {displayedPassengers.length} nga {passengers.length} pasagjerë
          </span>
          <button
            onClick={fetchPassengers}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <RefreshCw size={14} />
            Rifresko
          </button>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <PassengerFormModal
          key="add-passenger"
          isEdit={false}
          onSubmit={handleAddPassenger}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showEditModal && selectedPassenger && (
        <PassengerFormModal
          key={`edit-passenger-${selectedPassenger.id}`}
          isEdit={true}
          initialData={selectedPassenger}
          onSubmit={handleEditPassenger}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPassenger(null);
          }}
        />
      )}

      {showDeleteModal && selectedPassenger && (
        <DeleteConfirmModal
          passenger={selectedPassenger}
          onConfirm={handleDeletePassenger}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedPassenger(null);
          }}
          loading={deleteLoading}
        />
      )}

      {showProfileModal && selectedPassenger && (
        <PassengerProfile
          passenger={selectedPassenger}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedPassenger(null);
          }}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
          exchangeRate={exchangeRate}
        />
      )}

      {showPasswordModal && selectedPassenger && (
        <SetPasswordModal
          passenger={selectedPassenger}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedPassenger(null);
          }}
          onSave={handleSetPassword}
          loading={passwordLoading}
        />
      )}
    </div>
  );
};

export default PassengersList;
